import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminMemberDetail.scss';

const DEFAULT_MEMBERSHIP_FORM = {
  membership_tier: 'tour_member',
  membership_status: 'active',
  is_founding_member: false,
  member_since: ''
};

export default function AdminMemberDetail() {
  const { id } =
    useParams();

  const navigate =
    useNavigate();

  const apiBaseUrl =
    useMemo(() => {
      const value =
        process.env
          .REACT_APP_API_URL ||
        '';

      return value.endsWith('/')
        ? value.slice(
            0,
            -1
          )
        : value;
    }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    member,
    setMember
  ] = useState(null);

  const [
    stats,
    setStats
  ] = useState({
    rounds: 0,
    events: 0
  });

  const [
    rounds,
    setRounds
  ] = useState([]);

  const [
    events,
    setEvents
  ] = useState([]);

  const [
    activeTab,
    setActiveTab
  ] = useState(
    'overview'
  );

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState('');

  const [
    editingMembership,
    setEditingMembership
  ] = useState(false);

  const [
    savingMembership,
    setSavingMembership
  ] = useState(false);

  const [
    membershipForm,
    setMembershipForm
  ] = useState(
    DEFAULT_MEMBERSHIP_FORM
  );

  /*
  |--------------------------------------------------------------------------
  | Date Helpers
  |--------------------------------------------------------------------------
  */

  const formatDate = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '—';
    }

    return date.toLocaleDateString(
      undefined,
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    );
  };

  const formatDateTime = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '—';
    }

    return date.toLocaleString(
      undefined,
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }
    );
  };

  const toDateInput = (
    value
  ) => {
    if (!value) {
      return '';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '';
    }

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(
        2,
        '0'
      );

    const day =
      String(
        date.getDate()
      ).padStart(
        2,
        '0'
      );

    return `${year}-${month}-${day}`;
  };

  const formatLabel = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return String(value)
      .replaceAll(
        '_',
        ' '
      )
      .replace(
        /\b\w/g,
        (letter) =>
          letter.toUpperCase()
      );
  };

  /*
  |--------------------------------------------------------------------------
  | Load Member
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const fetchMember =
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/members/${id}`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load member.'
            );
          }

          setMember(
            data.member
          );

          setStats(
            data.stats || {
              rounds: 0,
              events: 0
            }
          );

          setRounds(
            data.rounds || []
          );

          setEvents(
            data.events || []
          );

          setMembershipForm({
            membership_tier:
              data.member
                .membership_tier ||
              'tour_member',

            membership_status:
              data.member
                .membership_status ||
              'active',

            is_founding_member:
              Boolean(
                data.member
                  .is_founding_member
              ),

            member_since:
              toDateInput(
                data.member
                  .member_since
              )
          });
        } catch (err) {
          setError(
            err.message ||
              'Unable to load member.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchMember();
  }, [
    apiBaseUrl,
    id,
    token
  ]);

  /*
  |--------------------------------------------------------------------------
  | Membership Form
  |--------------------------------------------------------------------------
  */

  const handleMembershipChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked
    } = event.target;

    setMembershipForm(
      (previous) => ({
        ...previous,

        [name]:
          type ===
          'checkbox'
            ? checked
            : value
      })
    );
  };

  const cancelMembershipEdit =
    () => {
      setEditingMembership(
        false
      );

      setMembershipForm({
        membership_tier:
          member
            ?.membership_tier ||
          'tour_member',

        membership_status:
          member
            ?.membership_status ||
          'active',

        is_founding_member:
          Boolean(
            member
              ?.is_founding_member
          ),

        member_since:
          toDateInput(
            member
              ?.member_since
          )
      });

      setError('');
    };

  const saveMembership =
    async () => {
      setSavingMembership(
        true
      );

      setError('');
      setSuccess('');

      try {
        const payload = {
          ...membershipForm,

          member_since:
            membershipForm
              .member_since
              ? new Date(
                  `${membershipForm.member_since}T12:00:00`
                ).toISOString()
              : null
        };

        const response =
          await fetch(
            `${apiBaseUrl}/admin/members/${id}/membership`,
            {
              method:
                'PATCH',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to update membership.'
          );
        }

        setMember(
          (previous) => ({
            ...previous,
            ...data.member,

            account_status:
              data.member
                .membership_status
          })
        );

        setMembershipForm({
          membership_tier:
            data.member
              .membership_tier ||
            'tour_member',

          membership_status:
            data.member
              .membership_status ||
            'active',

          is_founding_member:
            Boolean(
              data.member
                .is_founding_member
            ),

          member_since:
            toDateInput(
              data.member
                .member_since
            )
        });

        setEditingMembership(
          false
        );

        setSuccess(
          'Membership updated successfully.'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to update membership.'
        );
      } finally {
        setSavingMembership(
          false
        );
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Loading / Error
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="admin-member-detail-loading">
        Loading member...
      </div>
    );
  }

  if (!member) {
    return (
      <div className="admin-member-detail-loading">
        {error ||
          'Member not found.'}
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="admin-member-detail">

      {/* --------------------------------------------------
          Header
      -------------------------------------------------- */}

      <section className="admin-member-detail__heading">

        <div>
          <p>
            Member Management
          </p>

          <h2>
            {member.first_name}
            {' '}
            {member.last_name}
          </h2>

          <span>
            {member.email}
          </span>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate(
              '/admin/members'
            )
          }
        >
          ← Back to Members
        </button>

      </section>

      {/* --------------------------------------------------
          Messages
      -------------------------------------------------- */}

      {error ? (
        <div className="admin-member-detail__error">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="admin-member-detail__success">
          {success}
        </div>
      ) : null}

      {/* --------------------------------------------------
          Metrics
      -------------------------------------------------- */}

      <section className="admin-member-detail-metrics">

        <div>
          <span>
            Member ID
          </span>

          <strong>
            #{member.user_id}
          </strong>

          <small>
            Platform account
          </small>
        </div>

        <div>
          <span>
            Rounds
          </span>

          <strong>
            {stats.rounds}
          </strong>

          <small>
            Recorded rounds
          </small>
        </div>

        <div>
          <span>
            Events
          </span>

          <strong>
            {stats.events}
          </strong>

          <small>
            Registrations
          </small>
        </div>

        <div>
          <span>
            Membership
          </span>

          <strong className="admin-member-detail-metrics__status">
            {formatLabel(
              member
                .membership_status ||
                member
                  .account_status ||
                'active'
            )}
          </strong>

          <small>
            Current status
          </small>
        </div>

      </section>

      {/* --------------------------------------------------
          Tabs
      -------------------------------------------------- */}

      <nav className="admin-member-tabs">

        <button
          type="button"
          className={
            activeTab ===
            'overview'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'overview'
            )
          }
        >
          Overview
        </button>

        <button
          type="button"
          className={
            activeTab ===
            'rounds'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'rounds'
            )
          }
        >
          Rounds

          <span>
            {rounds.length}
          </span>
        </button>

        <button
          type="button"
          className={
            activeTab ===
            'events'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'events'
            )
          }
        >
          Events

          <span>
            {events.length}
          </span>
        </button>

      </nav>

      {/* --------------------------------------------------
          Overview
      -------------------------------------------------- */}

      {activeTab ===
      'overview' ? (
        <div className="admin-member-overview">

          {/* Member Info */}

          <section className="admin-member-detail-card">

            <div className="admin-member-detail-card__header">

              <div>
                <h3>
                  Member Information
                </h3>

                <p>
                  Profile and account
                  information.
                </p>
              </div>

            </div>

            <div className="admin-member-detail-grid">

              <div>
                <span>
                  First Name
                </span>

                <strong>
                  {member.first_name ||
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Last Name
                </span>

                <strong>
                  {member.last_name ||
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Email
                </span>

                <strong>
                  {member.email ||
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Birthday
                </span>

                <strong>
                  {formatDate(
                    member.birthday
                  )}
                </strong>
              </div>

              <div>
                <span>
                  City
                </span>

                <strong>
                  {member.city ||
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Province
                </span>

                <strong>
                  {member.province ||
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Country
                </span>

                <strong>
                  {member.country ||
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Member Since
                </span>

                <strong>
                  {formatDate(
                    member
                      .member_since
                  )}
                </strong>
              </div>

            </div>

          </section>

          {/* Golf Profile */}

          <section className="admin-member-detail-card">

            <div className="admin-member-detail-card__header">

              <div>
                <h3>
                  Golf Profile
                </h3>

                <p>
                  Current member
                  performance data.
                </p>
              </div>

            </div>

            <div className="admin-member-detail-grid admin-member-detail-grid--stats">

              <div>
                <span>
                  Handicap
                </span>

                <strong>
                  {member.handicap ??
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Average Score
                </span>

                <strong>
                  {member.average_score ??
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Average Pars
                </span>

                <strong>
                  {member.average_pars ??
                    '—'}
                </strong>
              </div>

              <div>
                <span>
                  Rounds Played
                </span>

                <strong>
                  {stats.rounds}
                </strong>
              </div>

            </div>

          </section>

          {/* Membership */}

          <section className="admin-member-detail-card">

            <div className="admin-member-detail-card__header admin-member-detail-card__header--actions">

              <div>
                <h3>
                  Membership
                </h3>

                <p>
                  Membership access,
                  tier and founding
                  status.
                </p>
              </div>

              {!editingMembership ? (
                <button
                  type="button"
                  className="admin-member-membership-edit"
                  onClick={() => {
                    setEditingMembership(
                      true
                    );

                    setSuccess('');
                  }}
                >
                  Edit Membership
                </button>
              ) : null}

            </div>

            {!editingMembership ? (

              <div className="admin-member-detail-grid">

                <div>
                  <span>
                    Tier
                  </span>

                  <strong>
                    {formatLabel(
                      member
                        .membership_tier
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Status
                  </span>

                  <strong>
                    {formatLabel(
                      member
                        .membership_status
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Founding Member
                  </span>

                  <strong>
                    {member
                      .is_founding_member
                      ? 'Yes'
                      : 'No'}
                  </strong>
                </div>

                <div>
                  <span>
                    Member Since
                  </span>

                  <strong>
                    {formatDate(
                      member
                        .member_since
                    )}
                  </strong>
                </div>

              </div>

            ) : (

              <div className="admin-member-membership-form">

                <div className="admin-member-membership-field">

                  <label>
                    Membership Tier
                  </label>

                  <select
                    name="membership_tier"
                    value={
                      membershipForm
                        .membership_tier
                    }
                    onChange={
                      handleMembershipChange
                    }
                  >
                    <option value="tour_member">
                      Tour Member
                    </option>

                    <option value="founding_member">
                      Founding Member
                    </option>
                  </select>

                </div>

                <div className="admin-member-membership-field">

                  <label>
                    Membership Status
                  </label>

                  <select
                    name="membership_status"
                    value={
                      membershipForm
                        .membership_status
                    }
                    onChange={
                      handleMembershipChange
                    }
                  >
                    <option value="active">
                      Active
                    </option>

                    <option value="inactive">
                      Inactive
                    </option>

                    <option value="cancelled">
                      Cancelled
                    </option>
                  </select>

                </div>

                <div className="admin-member-membership-field">

                  <label>
                    Member Since
                  </label>

                  <input
                    type="date"
                    name="member_since"
                    value={
                      membershipForm
                        .member_since
                    }
                    onChange={
                      handleMembershipChange
                    }
                  />

                </div>

                <label className="admin-member-membership-checkbox">

                  <input
                    type="checkbox"
                    name="is_founding_member"
                    checked={
                      membershipForm
                        .is_founding_member
                    }
                    onChange={
                      handleMembershipChange
                    }
                  />

                  <div>
                    <strong>
                      Founding Member
                    </strong>

                    <span>
                      Apply founding
                      member designation
                      to this account.
                    </span>
                  </div>

                </label>

                <div className="admin-member-membership-actions">

                  <button
                    type="button"
                    className="secondary"
                    onClick={
                      cancelMembershipEdit
                    }
                    disabled={
                      savingMembership
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="primary"
                    onClick={
                      saveMembership
                    }
                    disabled={
                      savingMembership
                    }
                  >
                    {savingMembership
                      ? 'Saving...'
                      : 'Save Membership'}
                  </button>

                </div>

              </div>

            )}

          </section>

        </div>
      ) : null}

      {/* --------------------------------------------------
          Rounds
      -------------------------------------------------- */}

      {activeTab ===
      'rounds' ? (
        <section className="admin-member-detail-card">

          <div className="admin-member-detail-card__header">

            <div>
              <h3>
                Recent Rounds
              </h3>

              <p>
                Latest recorded rounds
                for this member.
              </p>
            </div>

          </div>

          {rounds.length ===
          0 ? (
            <div className="admin-member-detail-empty">

              <strong>
                No rounds recorded
              </strong>

              <span>
                This member has not
                recorded a round yet.
              </span>

            </div>
          ) : (
            <div className="admin-member-table-wrapper">

              <table className="admin-member-table">

                <thead>
                  <tr>
                    <th>
                      Date
                    </th>

                    <th>
                      Course
                    </th>

                    <th>
                      Score
                    </th>

                    <th>
                      Block
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rounds.map(
                    (round) => (
                      <tr
                        key={
                          round.round_id
                        }
                      >
                        <td>
                          {formatDate(
                            round.game_date
                          )}
                        </td>

                        <td>
                          <strong>
                            {round.course_name ||
                              '—'}
                          </strong>
                        </td>

                        <td>
                          {round.total_score ??
                            round.total ??
                            '—'}
                        </td>

                        <td>
                          {formatLabel(
                            round.block
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

              </table>

            </div>
          )}

        </section>
      ) : null}

      {/* --------------------------------------------------
          Events
      -------------------------------------------------- */}

      {activeTab ===
      'events' ? (
        <section className="admin-member-detail-card">

          <div className="admin-member-detail-card__header">

            <div>
              <h3>
                Event Activity
              </h3>

              <p>
                Event registrations
                and booking history.
              </p>
            </div>

          </div>

          {events.length ===
          0 ? (
            <div className="admin-member-detail-empty">

              <strong>
                No event registrations
              </strong>

              <span>
                This member has not
                registered for an
                event yet.
              </span>

            </div>
          ) : (
            <div className="admin-member-table-wrapper">

              <table className="admin-member-table">

                <thead>
                  <tr>
                    <th>
                      Event
                    </th>

                    <th>
                      Date
                    </th>

                    <th>
                      Type
                    </th>

                    <th>
                      Registration
                    </th>

                    <th>
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {events.map(
                    (
                      event,
                      index
                    ) => (
                      <tr
                        key={`${event.event_id}-${event.slot_id || index}`}
                      >
                        <td>
                          <strong>
                            {event.title}
                          </strong>
                        </td>

                        <td>
                          {formatDateTime(
                            event.starts_at ||
                              event.datetime
                          )}
                        </td>

                        <td>
                          {formatLabel(
                            event.type
                          )}
                        </td>

                        <td>
                          {formatLabel(
                            event.registration_type
                          )}
                        </td>

                        <td>
                          <span className="admin-member-event-status">
                            {formatLabel(
                              event.status
                            )}
                          </span>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

              </table>

            </div>
          )}

        </section>
      ) : null}

    </div>
  );
}